import { useEffect, useState, useCallback, useRef } from 'react'
import { Message, SelectPicker } from 'rsuite'
import { useForm } from 'react-hook-form'
import ImageUploader from '../../../ui_reusable/ImageUploader'
import { StandardButton } from '../../../ui_reusable/styles/Button.styles'
import useStandardToaster from '../../../ui_reusable/StandardToaster'
import {
  ButtonContainerMargin,
  Label,
  FormGroup,
  FormSection,
  GridLayout,
  Container,
  SuccessContainer,
} from '../../../ui_reusable/styles/CreateInputUtils.styles'
import { StandardInput } from '../../../ui_reusable/StandardInputs'
import CreatorAccordion from '../../../ui_reusable/CreatorAccordion'
import CheckmarkAnimation from '../../../ui_reusable/CheckmarkAnimation'
import {
  ErrorMessage,
  ServerErrorMessage,
} from '../../../ui_reusable/styles/CreateInputUtils.styles'

const ClientForm = ({ setRefresh }) => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [fileList, setFileList] = useState([])
  const { pushToast } = useStandardToaster()
  const [uploading, setUploading] = useState(false)
  const panelRef = useRef(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [, setRemoveContainer] = useState(true)
  const [serverError, setServerError] = useState(null)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    clearErrors,
    setError, // Add this
  } = useForm({
    defaultValues: {
      name: '',
      client_id: '',
      kam_id: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  useEffect(() => {
    const getKAMs = async () => {
      setLoading(true)
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users?user_type=kam`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          },
        )

        if (response.status === 403) {
          throw new Error('Access denied: Not authorized to view KAMs')
        }

        const res = await response.json()

        if (res.data) {
          setUsers(res.data)
        }
      } catch (error) {
        console.error('Failed to fetch KAMs:', error)
        pushToast(
          <Message type="error">
            {error.message || 'Failed to load KAM list: Access Denied'}
          </Message>,
          { placement: 'topCenter', duration: 5000 },
        )
        setUsers([])
      } finally {
        setLoading(false)
      }
    }

    getKAMs()
    /* eslint-disable-next-line */
  }, [])

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const handleFileChange = useCallback(
    (files) => {
      if (!files || files.length === 0) return
      setUploading(true)
      const file = files[files.length - 1]

      setUploading(false)
      if (!file.blobFile.type.startsWith('image/')) {
        pushToast(<Message type="error">Please upload an image file</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })

        return
      }

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }

      const objectUrl = URL.createObjectURL(file.blobFile)

      setPreviewUrl(objectUrl)
      setFileList([file])
      pushToast(<Message type="success">Image selected successfully</Message>, {
        placement: 'topCenter',
        duration: 5000,
      })
    },
    [previewUrl, pushToast],
  )

  const handleRemove = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }

    setFileList([])
    pushToast(<Message type="info">Image removed</Message>, {
      placement: 'topCenter',
      duration: 5000,
    })
  }, [previewUrl, pushToast])

  const onSubmit = async (formData) => {
    // Add KAM validation before submission
    if (!formData.kam_id) {
      setError('kam_id', {
        type: 'required',
        message: 'Please select a Key Account Manager',
      })

      return
    }

    setServerError(null)

    const formDataPayload = new FormData()

    formDataPayload.append('name', formData.name)
    formDataPayload.append('client_id', formData.client_id)
    formDataPayload.append('kam_id', formData.kam_id || 0)
    if (fileList.length > 0) {
      formDataPayload.append('logo', fileList[0].blobFile)
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/clients/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formDataPayload,
        },
      )

      const responseData = await response.json()

      if (response.status === 201) {
        setRemoveContainer(false)
        setShowSuccess(true)
        await setRefresh(Date.now())
        reset()
        setPreviewUrl(null)
        setFileList([])

        setTimeout(() => {
          if (panelRef.current) {
            panelRef.current.tryCollapse()
            setShowSuccess(false)
            setTimeout(() => setRemoveContainer(true), 300)
          }
        }, 3000)
      } else {
        // Format server error messages
        if (typeof responseData.message === 'object') {
          const errorMessages = Object.entries(responseData.message)
            .map(([key, messages]) => `${key}: ${messages.join(', ')}`)
            .join('\n')

          setServerError(errorMessages)
        } else {
          setServerError(responseData.message || 'Failed to create client')
        }
      }
    } catch (error) {
      setServerError(error.message || 'An unexpected error occurred')
    }
  }

  return (
    <CreatorAccordion id={'createClientAccordion'}>
      <CreatorAccordion.Panel ref={panelRef} header="Create new client">
        <Container>
          <GridLayout>
            <FormSection>
              <form onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                  <Label htmlFor="name">Client name</Label>
                  <StandardInput
                    id="name"
                    {...register('name', {
                      required: 'Client name is required',
                    })}
                  />
                  <ErrorMessage>
                    {errors.name?.message || '\u00A0'}
                  </ErrorMessage>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="client_id">Client ID</Label>
                  <StandardInput
                    id="client_id"
                    {...register('client_id', {
                      required: 'Client ID is required',
                    })}
                  />
                  <ErrorMessage>
                    {errors.client_id?.message || '\u00A0'}
                  </ErrorMessage>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="kam_id">Key Account Manager (KAM)</Label>
                  <SelectPicker
                    id="kam_id"
                    searchable={false}
                    cleanable={false}
                    style={{ minWidth: 300 }}
                    data={users.map((elem) => ({
                      label: elem.name,
                      value: elem.id,
                    }))}
                    value={watch('kam_id')}
                    onChange={(value) => {
                      setValue('kam_id', value, {
                        shouldValidate: true,
                      })
                      if (!value) {
                        setError('kam_id', {
                          type: 'required',
                          message: 'Please select a Key Account Manager',
                        })
                      } else {
                        clearErrors('kam_id')
                      }
                    }}
                    disabled={loading}
                    placeholder={
                      loading
                        ? 'Loading Key Account Managers...'
                        : 'Select Key Account Manager'
                    }
                  />
                  <ErrorMessage>
                    {errors.kam_id?.message || '\u00A0'}
                  </ErrorMessage>
                </FormGroup>
              </form>
            </FormSection>
            <FormSection>
              <ImageUploader
                onFileSelect={handleFileChange}
                onRemove={handleRemove}
                previewUrl={previewUrl}
                fileList={fileList}
                uploading={uploading}
                headerText="Client logo"
              />
            </FormSection>
          </GridLayout>
          <ButtonContainerMargin>
            <StandardButton onClick={handleSubmit(onSubmit)}>
              Save client
            </StandardButton>
          </ButtonContainerMargin>
          {(serverError || showSuccess) && (
            <SuccessContainer>
              {serverError ? (
                <ServerErrorMessage>{serverError}</ServerErrorMessage>
              ) : (
                <CheckmarkAnimation
                  show={showSuccess}
                  text="Client created successfully!"
                />
              )}
            </SuccessContainer>
          )}
        </Container>
      </CreatorAccordion.Panel>
    </CreatorAccordion>
  )
}

export default ClientForm
